import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { styleMap } from "lit/directives/style-map.js";
import { baseStyles } from "../styles/base";
import { close, search } from "../svg";
import { appRouter } from "../router";
import { StateController } from "../state";

@customElement("umb-market-search")
export class UmbMarketSearch extends LitElement {
  #value: string = "";

  readonly #minimumQueryLength = 2;

  set _value(val: string) {
    this.#value = val;
    this._submitted = this.#value !== "";
  }

  @state()
  get _value(): string {
    return this.#value;
  }

  @state()
  _submitted = false;

  @property({ type: Object })
  location = appRouter?.location;

  connectedCallback(): void {
    super.connectedCallback();
    window.addEventListener("popstate", this.#checkViewState);

    if (StateController.current.isBackoffice) {
      this.setAttribute("backoffice", "");
    }

    // little clunky, but we need to manage cases where a search string may have a / character
    // so is essentially a path segment of unknown length. We can get the string,
    // trim the leading /, and set as the current search value
    if (StateController.isSearchView) {
      const searchString = window.location?.pathname.split("/search")?.pop();
      if (searchString?.length) {
        this._value = searchString.replace(/^\//, "");
      }
    }
  }

  disconnectedCallback(): void {
    window.removeEventListener("popstate", this.#checkViewState);
    super.disconnectedCallback();
  }

  #checkViewState = () => {
    this._value = StateController.isSearchView ? this._value : "";
  };

  #clearSearch() {
    this._value =
      StateController.current.queryKey =
      StateController.current.query =
      StateController.current.pageTitle =
        "";

    // when tag/search is cleared, route back to the parent node, and display all items
    window.history.pushState(null, "", `/all`);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }

  #submitSearch(e: SubmitEvent) {
    e.preventDefault();
    if (this._value?.length < this.#minimumQueryLength) return;
    this.shadowRoot?.getElementById("search")?.click();
  }

  #onChange(e: any) {
    this._value = e.target?.value;
    this._submitted = false;
  }

  render() {
    const searchStyles = { display: this._submitted ? "none" : undefined };
    const clearStyles = { display: this._submitted ? undefined : "none" };

    return html`
      <form id="form" @submit=${this.#submitSearch}>
        <input
          id="searchTerm"
          type="search"
          placeholder="Search by name or creator"
          .value=${this._value}
          @change=${this.#onChange}
        />
        <button aria-label="Search" style=${styleMap(searchStyles)}>
          ${search}
        </button>
        <button
          aria-label="Clear"
          type="button"
          style=${styleMap(clearStyles)}
          @click=${this.#clearSearch}
        >
          ${close}
        </button>
      </form>

      <a id="search" href=${`/search/${this._value}`} hidden>search</a>
    `;
  }

  static styles = [
    baseStyles,
    css`
      :host {
        width: 100%;
        max-width: 350px;
      }

      input {
        border-radius: 10px;
        border: none;
        width: 100%;
        font-family: inherit;
        font-size: var(--s-spacer);
        height: var(--xl-spacer);
        color: var(--uui-color-text);
        padding: var(--xs-spacer) calc(var(--s-spacer) + 30px) var(--xs-spacer)
          var(--s-spacer);
      }

      [type="search"]::-webkit-search-cancel-button,
      [type="search"]::-webkit-search-decoration {
        -webkit-appearance: none;
        appearance: none;
      }

      @media (min-width: 767px) {
        :host {
          margin-right: 66px;
        }

        :host([backoffice]) {
          margin-right: 0;
        }
      }

      @media (min-width: 992px) {
        :host {
          max-width: 400px;
          margin-right: 0;
        }
      }

      input:active,
      input:focus {
        outline-color: var(--uui-color-default);
      }

      form {
        position: relative;
      }

      button {
        display: flex;
        align-items: center;
        width: var(--l-spacer);
        height: var(--l-spacer);
        background: var(--uui-color-surface);
        border: 0;
        border-radius: 50%;
        position: absolute;
        top: 50%;
        right: var(--xs-spacer);
        transform: translateY(-50%);

        --fill: var(--uui-color-default);
      }

      button svg {
        width: var(--s-spacer);
      }
    `,
  ];
}
